import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Select,
  Input,
  DatePicker,
  Tag,
  Space,
  Modal,
  notification,
  Tooltip,
  Badge,
  Dropdown,
  Menu,
  Statistic,
  Row,
  Col,
  Form,
 } from 'antd';
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ExportOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ContactsOutlined,
  CommentOutlined
} from '@ant-design/icons';
import moment from 'moment';
import messageService from '../../services/messageService'; // Import the updated service

const { Option } = Select;
const { Search } = Input;
const { RangePicker } = DatePicker;
const { confirm } = Modal;
const { TextArea } = Input;

const ContactFormsList = () => {
  const [contactForms, setContactForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0
  });
  const [filters, setFilters] = useState({
    status: null,
    service: null,
    source: null,
    search: '',
    dateRange: null
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [stats, setStats] = useState({});
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [notesModalVisible, setNotesModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [bulkUpdateVisible, setBulkUpdateVisible] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [notesForm] = Form.useForm();

  // Service options
  const services = [
    'Linen and towel hire',
    'Cleaning',
    'Baby Equipment',
    'Toiletries',
    'Register',
    'Other'
  ];

  // Status options
  const statusOptions = {
    'new': { label: 'New', color: 'blue' },
    'contacted': { label: 'Contacted', color: 'orange' },
    'converted': { label: 'Converted', color: 'green' },
    'closed': { label: 'Closed', color: 'gray' }
  };

  // Source options
  const sourceOptions = {
    'iframe': { label: 'Website Iframe', color: 'purple' },
    'direct': { label: 'Direct Access', color: 'cyan' }
  };

  // Contact methods
  const contactMethods = ['Email', 'Phone', 'WhatsApp', 'In Person', 'Other'];

  // Fetch contact forms using the service
  const fetchContactForms = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize,
        ...filters
      };

      if (filters.dateRange && filters.dateRange.length === 2) {
        params.start_date = filters.dateRange[0].format('YYYY-MM-DD');
        params.end_date = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await messageService.getContactForms(params);

      if (response.success) {
        setContactForms(response.data);
        setPagination({
          ...pagination,
          total: response.total,
          current: response.current_page,
          pageSize: response.per_page
        });
        setStats(response.stats);
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to fetch contact forms'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContactForms();
  }, [pagination.current, pagination.pageSize, filters]);

  // Handle table change
  const handleTableChange = (newPagination, tableFilters, sorter) => {
    setPagination(newPagination);
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      status: null,
      service: null,
      source: null,
      search: '',
      dateRange: null
    });
  };

  // Handle view details
  const handleView = (record) => {
    setSelectedRecord(record);
    setViewModalVisible(true);
  };

  // Handle status update using the service
  const handleStatusUpdate = async (id, status, notes = '') => {
    try {
      const updateData = { status };
      if (notes) {
        updateData.admin_notes = notes;
      }

      const response = await messageService.updateContactForm(id, updateData);

      if (response.success) {
        notification.success({
          message: 'Success',
          description: 'Status updated successfully'
        });
        fetchContactForms();
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to update status'
      });
    }
  };

  // Handle delete using the service
  const handleDelete = (id) => {
    confirm({
      title: 'Are you sure you want to delete this contact form?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await messageService.deleteContactForm(id);
          notification.success({
            message: 'Success',
            description: 'Contact form deleted successfully'
          });
          fetchContactForms();
        } catch (error) {
          notification.error({
            message: 'Error',
            description: error.message || 'Failed to delete contact form'
          });
        }
      }
    });
  };

  // Handle bulk update using the service
  const handleBulkUpdate = async () => {
    if (!bulkStatus || selectedRowKeys.length === 0) {
      notification.warning({
        message: 'Warning',
        description: 'Please select a status and at least one record'
      });
      return;
    }

    try {
      const response = await messageService.bulkUpdateContactForms(selectedRowKeys, {
        status: bulkStatus
      });

      if (response.success) {
        notification.success({
          message: 'Success',
          description: response.message || 'Contact forms updated successfully'
        });
        setSelectedRowKeys([]);
        setBulkUpdateVisible(false);
        setBulkStatus('');
        fetchContactForms();
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to update contact forms'
      });
    }
  };

  // Handle export using the service
  const handleExport = async () => {
    try {
      const params = { ...filters };
      if (filters.dateRange && filters.dateRange.length === 2) {
        params.start_date = filters.dateRange[0].format('YYYY-MM-DD');
        params.end_date = filters.dateRange[1].format('YYYY-MM-DD');
      }
      
      const blob = await messageService.exportContactForms(params);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contact-forms-${moment().format('YYYY-MM-DD')}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      notification.success({
        message: 'Success',
        description: 'Contact forms exported successfully'
      });
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to export contact forms'
      });
    }
  };

  // Handle mark as contacted using the service
  const handleMarkAsContacted = async (id, contactMethod, notes = '') => {
    try {
      const response = await messageService.markContactFormAsContacted(id, contactMethod, notes);
      
      if (response.success) {
        notification.success({
          message: 'Success',
          description: 'Contact form marked as contacted'
        });
        fetchContactForms();
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to mark as contacted'
      });
    }
  };

  // Handle add notes
  const handleAddNotes = (record) => {
    setSelectedRecord(record);
    notesForm.setFieldsValue({
      notes: record.admin_notes || ''
    });
    setNotesModalVisible(true);
  };

  // Save notes
  const handleSaveNotes = async () => {
    try {
      const values = await notesForm.validateFields();
      const response = await messageService.addContactFormNotes(selectedRecord.id, values.notes);
      
      if (response.success) {
        notification.success({
          message: 'Success',
          description: 'Notes saved successfully'
        });
        setNotesModalVisible(false);
        fetchContactForms();
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to save notes'
      });
    }
  };

  // Get priority color
  const getPriorityColor = (record) => {
    const hours = moment().diff(moment(record.created_at), 'hours');
    const highPriorityServices = ['Register', 'Cleaning'];
    
    if (record.status === 'new') {
      if (highPriorityServices.includes(record.service)) {
        return hours > 24 ? 'red' : 'orange';
      }
      return hours > 48 ? 'orange' : 'blue';
    }
    return 'green';
  };

  // Table columns
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div className="font-medium">{text}</div>
          <div className="text-xs text-gray-500">
            {moment(record.created_at).format('DD/MM/YYYY HH:mm')}
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div className="flex items-center mb-1">
            <MailOutlined className="mr-1 text-gray-400" />
            <span className="text-sm">{record.email}</span>
          </div>
          <div className="flex items-center">
            <PhoneOutlined className="mr-1 text-gray-400" />
            <span className="text-sm">{record.phone}</span>
          </div>
        </div>
      ),
    },
    {
      title: 'Service',
      dataIndex: 'service',
      key: 'service',
      render: (service) => (
        <Tag color="blue">{service}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const config = statusOptions[status];
        return (
          <Badge
            color={getPriorityColor(record)}
            text={<Tag color={config.color}>{config.label}</Tag>}
          />
        );
      },
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      render: (source) => {
        const config = sourceOptions[source];
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      render: (message) => (
        <div className="max-w-xs truncate">
          {message || 'No message'}
        </div>
      ),
    },
    {
      title: 'Time Since',
      key: 'time_since',
      render: (_, record) => moment(record.created_at).fromNow(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          
          <Dropdown
            overlay={
              <Menu>
                <Menu.SubMenu key="status" title="Change Status" icon={<EditOutlined />}>
                  {Object.entries(statusOptions).map(([key, config]) => (
                    <Menu.Item
                      key={key}
                      onClick={() => handleStatusUpdate(record.id, key)}
                    >
                      <Tag color={config.color}>{config.label}</Tag>
                    </Menu.Item>
                  ))}
                </Menu.SubMenu>
                <Menu.SubMenu key="contact" title="Mark as Contacted" icon={<ContactsOutlined />}>
                  {contactMethods.map(method => (
                    <Menu.Item
                      key={method}
                      onClick={() => handleMarkAsContacted(record.id, method)}
                    >
                      {method}
                    </Menu.Item>
                  ))}
                </Menu.SubMenu>
                <Menu.Item
                  key="notes"
                  icon={<CommentOutlined />}
                  onClick={() => handleAddNotes(record)}
                >
                  Add Notes
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <Button type="text" icon={<EditOutlined />} />
          </Dropdown>
          
          <Tooltip title="Delete">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Row selection
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Contact Forms</h1>
        <Space>
          <Button
            icon={<ExportOutlined />}
            onClick={handleExport}
          >
            Export
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchContactForms}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Forms"
              value={stats.total_forms || 0}
              prefix={<MailOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="New Forms"
              value={stats.new_forms || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Converted"
              value={stats.converted_forms || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Conversion Rate"
              value={stats.conversion_rate || 0}
              suffix="%"
              precision={1}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        {/* Filters */}
        <div className="mb-4">
          <Row gutter={16}>
            <Col span={6}>
              <Search
                placeholder="Search name, email, phone..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                allowClear
              />
            </Col>
            <Col span={4}>
              <Select
                placeholder="Status"
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                allowClear
                style={{ width: '100%' }}
              >
                {Object.entries(statusOptions).map(([key, config]) => (
                  <Option key={key} value={key}>{config.label}</Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <Select
                placeholder="Service"
                value={filters.service}
                onChange={(value) => handleFilterChange('service', value)}
                allowClear
                style={{ width: '100%' }}
              >
                {services.map(service => (
                  <Option key={service} value={service}>{service}</Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <Select
                placeholder="Source"
                value={filters.source}
                onChange={(value) => handleFilterChange('source', value)}
                allowClear
                style={{ width: '100%' }}
              >
                {Object.entries(sourceOptions).map(([key, config]) => (
                  <Option key={key} value={key}>{config.label}</Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <RangePicker
                value={filters.dateRange}
                onChange={(dates) => handleFilterChange('dateRange', dates)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={2}>
              <Button
                icon={<FilterOutlined />}
                onClick={resetFilters}
                title="Reset Filters"
              />
            </Col>
          </Row>
        </div>

        {/* Bulk Actions */}
        {selectedRowKeys.length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 rounded">
            <div className="flex items-center justify-between">
              <span>{selectedRowKeys.length} items selected</span>
              <Space>
                <Select
                  placeholder="Change Status"
                  value={bulkStatus}
                  onChange={setBulkStatus}
                  style={{ width: 150 }}
                >
                  {Object.entries(statusOptions).map(([key, config]) => (
                    <Option key={key} value={key}>{config.label}</Option>
                  ))}
                </Select>
                <Button type="primary" onClick={handleBulkUpdate}>
                  Update
                </Button>
                <Button onClick={() => setSelectedRowKeys([])}>
                  Clear
                </Button>
              </Space>
            </div>
          </div>
        )}

        {/* Table */}
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={contactForms}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* View Modal */}
      <Modal
        title="Contact Form Details"
        visible={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedRecord && (
          <div>
            <Row gutter={16}>
              <Col span={12}>
                <h4>Contact Information</h4>
                <p><strong>Name:</strong> {selectedRecord.name}</p>
                <p><strong>Email:</strong> {selectedRecord.email}</p>
                <p><strong>Phone:</strong> {selectedRecord.phone}</p>
              </Col>
              <Col span={12}>
                <h4>Details</h4>
                <p><strong>Service:</strong> {selectedRecord.service}</p>
                <p><strong>Status:</strong> <Tag color={statusOptions[selectedRecord.status].color}>{statusOptions[selectedRecord.status].label}</Tag></p>
                <p><strong>Source:</strong> <Tag color={sourceOptions[selectedRecord.source].color}>{sourceOptions[selectedRecord.source].label}</Tag></p>
                <p><strong>Submitted:</strong> {moment(selectedRecord.created_at).format('DD/MM/YYYY HH:mm:ss')}</p>
              </Col>
            </Row>
            
            {selectedRecord.message && (
              <div className="mt-4">
                <h4>Message</h4>
                <div className="p-3 bg-gray-50 rounded">
                  {selectedRecord.message}
                </div>
              </div>
            )}
            
            {selectedRecord.admin_notes && (
              <div className="mt-4">
                <h4>Admin Notes</h4>
                <div className="p-3 bg-blue-50 rounded">
                  {selectedRecord.admin_notes}
                </div>
              </div>
            )}
            
            <div className="mt-4">
              <h4>Technical Details</h4>
              <p><strong>IP Address:</strong> {selectedRecord.ip_address}</p>
              <p><strong>User Agent:</strong> <span className="text-xs">{selectedRecord.user_agent}</span></p>
              {selectedRecord.contacted_at && (
                <p><strong>Contacted At:</strong> {moment(selectedRecord.contacted_at).format('DD/MM/YYYY HH:mm:ss')}</p>
              )}
              {selectedRecord.assigned_user && (
                <p><strong>Assigned To:</strong> {selectedRecord.assigned_user.name}</p>
              )}
            </div>
            
            <div className="mt-6">
              <h4>Quick Actions</h4>
              <Space wrap>
                {Object.entries(statusOptions).map(([key, config]) => (
                  <Button
                    key={key}
                    type={selectedRecord.status === key ? "primary" : "default"}
                    size="small"
                    onClick={() => {
                      handleStatusUpdate(selectedRecord.id, key);
                      setViewModalVisible(false);
                    }}
                  >
                    Mark as {config.label}
                  </Button>
                ))}
                <Button
                  type="default"
                  size="small"
                  icon={<CommentOutlined />}
                  onClick={() => {
                    setViewModalVisible(false);
                    handleAddNotes(selectedRecord);
                  }}
                >
                  Add Notes
                </Button>
              </Space>
            </div>
            
            <div className="mt-4">
              <h4>Contact Methods</h4>
              <Space wrap>
                {contactMethods.map(method => (
                  <Button
                    key={method}
                    type="default"
                    size="small"
                    icon={<ContactsOutlined />}
                    onClick={() => {
                      handleMarkAsContacted(selectedRecord.id, method);
                      setViewModalVisible(false);
                    }}
                  >
                    Contacted via {method}
                  </Button>
                ))}
              </Space>
            </div>
          </div>
        )}
      </Modal>

      {/* Notes Modal */}
      <Modal
        title="Add/Edit Notes"
        visible={notesModalVisible}
        onOk={handleSaveNotes}
        onCancel={() => {
          setNotesModalVisible(false);
          notesForm.resetFields();
        }}
        width={600}
      >
        <Form form={notesForm} layout="vertical">
          <Form.Item
            name="notes"
            label="Admin Notes"
            rules={[
              { required: true, message: 'Please enter notes' },
              { min: 10, message: 'Notes must be at least 10 characters long' }
            ]}
          >
            <TextArea
              rows={6}
              placeholder="Enter admin notes here..."
              showCount
              maxLength={1000}
            />
          </Form.Item>
        </Form>
        
        {selectedRecord && (
          <div className="mt-4 p-3 bg-gray-50 rounded">
            <h5>Contact Form Summary:</h5>
            <p><strong>Name:</strong> {selectedRecord.name}</p>
            <p><strong>Service:</strong> {selectedRecord.service}</p>
            <p><strong>Status:</strong> <Tag color={statusOptions[selectedRecord.status].color}>{statusOptions[selectedRecord.status].label}</Tag></p>
            <p><strong>Submitted:</strong> {moment(selectedRecord.created_at).fromNow()}</p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ContactFormsList;